import React, { useState, useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useParams, useLocation } from 'react-router-dom';
import { orderService } from '../../data';
import { FaPrint } from 'react-icons/fa';

const GlobalPrintStyle = createGlobalStyle`
  @media print {
    body * {
      visibility: hidden !important;
    }
    .print-area, .print-area * {
      visibility: visible !important;
    }
    .print-area {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 100vw !important;
      background: white !important;
      z-index: 99999 !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .Overlay, .Popup, .overlay, .popup, .print-hide, [class*='Overlay'], [class*='Popup'] {
      display: none !important;
      visibility: hidden !important;
      position: static !important;
      margin: 0 !important;
      padding: 0 !important;
      box-shadow: none !important;
      background: none !important;
    }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: auto !important;
      height: auto !important;
      background: white !important;
    }
    table {
      width: 100% !important;
      table-layout: auto !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    td, th {
      word-break: break-word !important;
      white-space: normal !important;
      margin: 0 !important;
      padding: 0.1rem 0.1rem !important;
    }
    th:not(:first-child), td:not(:first-child) {
      width: 20px !important;
      font-size: 14px !important;
    }
    th:first-child, td:first-child {
      width: 50px !important;
    }
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Popup = styled.div`
  background: #fff;
  padding: 2rem;
  border-radius: 10px;
  min-width: 700px;
  max-width: 1100px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #4A3C31;
  cursor: pointer;
`;

const Title = styled.h2`
  color: #4A3C31;
  margin-bottom: 0.5rem;
  margin-top: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  color: #4A3C31;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #C5A572;
  border-radius: 4px;
`;

const Button = styled.button`
  background: #A68B5B;
  color: #fff;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-left: 1rem;
  &:hover { background: #4A3C31; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  table-layout: auto;
  @media print {
    width: 100% !important;
    max-width: 100% !important;
    font-size: 11px !important;
    margin-top: 0 !important;
  }
`;
const Th = styled.th`
  background: #4A3C31;
  color: #fff;
  padding: 0.5rem 1rem;
  @media print {
    padding: 0.2rem 0.3rem !important;
    font-size: 14px !important;
  }
`;
const Td = styled.td`
  border: 1px solid #E8D5B5;
  padding: 0.5rem 1rem;
  text-align: center;
  @media print {
    padding: 0.2rem 0.3rem !important;
    font-size: 14px !important;
  }
`;

const ProductTd = styled(Td)`
  text-align: left;
  max-width: 150px;
  white-space: normal;
  word-wrap: break-word;
  @media print {
    max-width: 90px !important;
    width: 90px !important;
    word-break: break-word !important;
    white-space: normal !important;
  }
`;

const DisabledTh = styled(Th)`
  background: #bdbdbd;
  color: #fff;
  opacity: 0.7;
`;
const DisabledTd = styled(Td)`
  background: #f5f5f5;
  color: #bdbdbd;
`;

const PrintButton = styled.button`
  background: #C5A572;
  color: #fff;
  border: none;
  padding: 0.4rem 1.2rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  margin-right: 1rem;
  &:hover { background: #4A3C31; }
  @media print {
    display: none;
  }
`;

const PrintArea = styled.div`
  margin: 0;
  padding: 0;
`;

const ScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  @media print {
    overflow: visible !important;
    width: 100% !important;
    max-width: 100% !important;
  }
`;

const PrintOnly = styled.div`
  display: none;
  @media print {
    display: block !important;
  }
`;

const HideOnPrint = styled.div`
  @media print {
    display: none !important;
  }
`;

const WeekSpacer = styled.div`
  display: none;
  @media print {
    display: block;
    height: 2em;
    margin: 0;
    padding: 0;
  }
`;

// Add custom days of week (Sunday=1, ..., Saturday=7)
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseLocalDate(str) {
  // str: 'YYYY-MM-DD'
  const [year, month, day] = str.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function getDatesInRange(start, end) {
  const dates = [];
  let current = parseLocalDate(start);
  const endDate = parseLocalDate(end);
  while (current <= endDate) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

// Helper to map JS getDay() to standing order day (Sunday=1, ..., Saturday=7)
function mapJsDayToStandingOrderDay(jsDay) {
  return jsDay + 1;
}

function getQuantityForProductOnDate(product, date, standingOrders) {
  // Find all standing orders for this product that cover the date
  const relevantOrders = standingOrders.filter(o => {
    // Check if this order covers the date
    const start = new Date(o.start_date + 'T00:00:00');
    const endD = o.end_date ? new Date(o.end_date + 'T00:00:00') : new Date('9999-12-31');
    return date >= start && date <= endD && o.days.some(d => d.product_name === (product.description || product.name));
  });
  if (relevantOrders.length === 0) return 0;
  // Use the most recent order (latest start_date)
  const mostRecentOrder = relevantOrders.sort((a, b) => new Date(b.start_date) - new Date(a.start_date))[0];
  // Map JS day to standing order day: Sunday=1, ..., Saturday=7
  const day = mapJsDayToStandingOrderDay(date.getDay());
  const dayOrder = mostRecentOrder.days.find(x => x.day === day && x.product_name === (product.description || product.name));
  return dayOrder ? dayOrder.quantity : 0;
}

function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const Projection = (props) => {
  // Use props if provided, otherwise fallback to URL params
  const { storeId: propStoreId, market: propMarket, onClose } = props;
  const { storeId: urlStoreId } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlMarket = params.get('market');
  const storeId = propStoreId || urlStoreId;
  const market = propMarket || urlMarket;

  const [products, setProducts] = useState([]);
  const [standingOrders, setStandingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const tableRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [prods, orders] = await Promise.all([
          orderService.getProducts(storeId, market),
          orderService.getStandingOrders(storeId, market)
        ]);
        setProducts(prods);
        setStandingOrders(orders);
      } catch (err) {
        setError('Failed to load products or standing orders.');
      } finally {
        setLoading(false);
      }
    };
    if (storeId && market) fetchData();
  }, [storeId, market]);

  let dates = [];
  if (submitted && startDate && endDate) {
    dates = getDatesInRange(startDate, endDate);
  }

  const handleClose = () => {
    if (onClose) onClose();
  };

  const today = new Date();
  today.setHours(0,0,0,0);
  const todayStr = today.toISOString().split('T')[0];

  const handlePrint = () => {
    if (tableRef.current) {
      window.print();
    }
  };

  return (
    <>
      <PrintArea ref={tableRef} className="print-area">
        <PrintOnly>
          {chunkArray(dates, 7).map((weekDates, weekIdx) => {
            console.log('Rendering print week table', weekIdx, weekDates.map(d => d.toISOString()));
            return (
              <React.Fragment key={weekIdx}>
                <ScrollContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Product</Th>
                        {weekDates.map(date => {
                          const isPast = date < today;
                          const DayHeader = isPast ? DisabledTh : Th;
                          return (
                            <DayHeader key={date.toISOString()}>
                              {`${date.getMonth() + 1}/${date.getDate()}`}<br />
                              <span style={{ fontSize: '1em', color: '#fff' }}>
                                {DAYS_OF_WEEK[date.getDay()]}
                              </span>
                            </DayHeader>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(product => (
                        <tr key={product.id || product.product_id}>
                          <ProductTd>{product.description || product.name}</ProductTd>
                          {weekDates.map(date => {
                            const isPast = date < today;
                            const Cell = isPast ? DisabledTd : Td;
                            return (
                              <Cell key={date.toISOString()}>{getQuantityForProductOnDate(product, date, standingOrders)}</Cell>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollContainer>
                <WeekSpacer />
              </React.Fragment>
            );
          })}
        </PrintOnly>
      </PrintArea>
      <Overlay>
        <GlobalPrintStyle />
        <Popup>
          {onClose && <CloseButton onClick={handleClose}>&times;</CloseButton>}
          {loading && <div>Loading...</div>}
          {error && <div style={{ color: 'red' }}>{error}</div>}
          {!loading && !error && !submitted && (
            <form onSubmit={e => { e.preventDefault(); setSubmitted(true); }}>
              <FormRow>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={e => {
                    const newStart = e.target.value;
                    setStartDate(newStart);
                    if (endDate && endDate < newStart) {
                      setEndDate('');
                    }
                  }}
                  required
                  min={todayStr}
                />
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  required
                  min={startDate || todayStr}
                />
                <Button type="submit">Print Summary</Button>
              </FormRow>
            </form>
          )}
          {!loading && !error && submitted && dates.length > 0 && (
            <>
              <HeaderRow>
                <Title>Summary</Title>
                <PrintButton onClick={handlePrint}>
                  <FaPrint /> Print
                </PrintButton>
              </HeaderRow>
              <HideOnPrint>
                <ScrollContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Product</Th>
                        {dates.map(date => {
                          const isPast = date < today;
                          const DayHeader = isPast ? DisabledTh : Th;
                          return (
                            <DayHeader key={date.toISOString()}>
                              {`${date.getMonth() + 1}/${date.getDate()}`}<br />
                              <span style={{ fontSize: '1em', color: '#fff' }}>
                                {DAYS_OF_WEEK[date.getDay()]}
                              </span>
                            </DayHeader>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(product => (
                        <tr key={product.id || product.product_id}>
                          <ProductTd>{product.description || product.name}</ProductTd>
                          {dates.map(date => {
                            const isPast = date < today;
                            const Cell = isPast ? DisabledTd : Td;
                            return (
                              <Cell key={date.toISOString()}>{getQuantityForProductOnDate(product, date, standingOrders)}</Cell>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollContainer>
              </HideOnPrint>
            </>
          )}
        </Popup>
      </Overlay>
    </>
  );
};

export default Projection; 